<template>
    <div :class="{ opened: isOpen, closed: !isOpen }" class="sc-chat-window">
        <ChatHeader
            :title="title"
            :new-messages-count="newMessagesCount"
            :image-url="titleImageUrl"
            :participants="participants"
            :participants-loading="participantsLoading"
            :on-close="onClose"
            :colors="colors"
            :current-window="currentWindow"
            :online="online"
            @switch-window="switchWindow"
        />
        <ChatProgress :active="loading || messagesLoading" :colors="colors" indeterminate />
        <OfflineStatus />
        <div class="sc-chat-window__content">
            <Conversations
                v-show="currentWindow === 'conversations'"
                ref="conversations"
                :colors="colors"
                :conversations="conversationList"
                :current-conversation="currentConversation"
                :on-conversation-switch="onConversationSwitch"
                :online="online"
                @loading="value => (loading = value)"
                @switch-window="switchWindow"
            />
            <UserList v-if="currentWindow === 'participants'" :participants="participants" :online="online" />
            <NewConversation
                v-if="currentWindow === 'new-conversation'"
                :colors="colors"
                :online="online"
                @user-selected="onUserSelected"
            />
            <MessageList
                v-show="currentWindow === 'messages'"
                :messages="messageList"
                :messages-loading="messagesLoading"
                :message-history-count="messageHistoryCount"
                :participants="participants"
                :show-typing-indicator="showTypingIndicator"
                :colors="colors"
                :always-scroll-to-bottom="alwaysScrollToBottom"
                :message-styling="messageStyling"
                @load-message-history="$emit('load-message-history')"
            />
        </div>
        <UserInput
            v-show="currentWindow === 'messages'"
            :show-emoji="showEmoji"
            :on-submit="onUserInputSubmit"
            :suggestions="getSuggestions()"
            :show-file="showFile"
            :placeholder="placeholder"
            :colors="colors"
            :disabled="messagesLoading || participantsLoading || !conversationList.length"
            @typing="$emit('typing')"
        />
        <div v-show="currentWindow === 'conversations'" class="sc-chat-window__footer">
            <button class="button is-primary is-small is-fullwidth" @click="switchWindow('new-conversation')">
                <e-icon icon="plus" class="m-r-xs" />
                New conversation
            </button>
        </div>
    </div>
</template>

<script>
import Conversations from './Conversations.vue';
import Header from './Header.vue';
import MessageList from './MessageList.vue';
import NewConversation from './NewConversation.vue';
import OfflineStatus from './OfflineStatus.vue';
import Progress from './Progress.vue';
import UserInput from './UserInput.vue';
import UserList from './UserList.vue';

export default {
    components: {
        ChatHeader: Header,
        ChatProgress: Progress,
        Conversations,
        MessageList,
        NewConversation,
        UserInput,
        UserList,
        OfflineStatus,
    },

    props: {
        showEmoji: {
            type: Boolean,
            default: false,
        },
        showFile: {
            type: Boolean,
            default: false,
        },
        participants: {
            type: Array,
            required: true,
        },
        participantsLoading: {
            type: Boolean,
            default: false,
        },
        newMessagesCount: {
            type: Number,
            default: () => 0,
        },
        title: {
            type: String,
            required: false,
            default: '',
        },
        titleImageUrl: {
            type: String,
            default: '',
        },
        onUserInputSubmit: {
            type: Function,
            required: true,
        },
        onConversationSwitch: {
            type: Function,
            required: true,
        },
        onClose: {
            type: Function,
            required: true,
        },
        messageList: {
            type: Array,
            default: () => [],
        },
        messagesLoading: {
            type: Boolean,
            default: false,
        },
        messageHistoryCount: {
            type: Number,
            default: 0,
        },
        currentConversation: {
            type: Number,
            required: true,
        },
        conversationList: {
            type: Array,
            default: () => [],
        },
        isOpen: {
            type: Boolean,
            default: () => false,
        },
        placeholder: {
            type: String,
            default: 'Write a reply',
        },
        showTypingIndicator: {
            type: Number,
            default: null,
        },
        colors: {
            type: Object,
            required: true,
        },
        alwaysScrollToBottom: {
            type: Boolean,
            required: true,
        },
        messageStyling: {
            type: Boolean,
            required: true,
        },
        online: {
            type: Object,
            default: () => {},
        },
    },

    emits: ['typing', 'load-message-history', 'switch-window'],

    data() {
        return {
            loading: false,
            currentWindow: 'conversations',
        };
    },

    watch: {
        isOpen(open) {
            if (!open) {
                return;
            }

            if (this.currentWindow !== 'conversations') {
                this.switchWindow('conversations');

                return;
            }

            this.$nextTick(() => this.$refs.conversations?.scrollToCurrentConversation());
        },

        currentWindow(window) {
            if (window === 'conversations') {
                this.$nextTick(() => this.$refs.conversations?.scrollToCurrentConversation());
            }
        },
    },

    methods: {
        switchWindow(window) {
            this.currentWindow = window;
            this.$emit('switch-window', window);
        },

        async onUserSelected(userId) {
            this.loading = true;
            try {
                await this.$store.dispatch('Conversation/openNewConversation', userId);
                this.switchWindow('messages');
            } catch (error) {
                const detail = error?.message || error?.response?.data?.message || '';
                const message = detail
                    ? `Could not start a new conversation. ${detail}`
                    : 'Could not start a new conversation.';
                this.$buefy.toast.open({
                    message,
                    type: 'is-danger',
                    duration: 5000,
                });
            } finally {
                this.loading = false;
            }
        },

        getSuggestions() {
            return this.messageList.length > 0 ? this.messageList[this.messageList.length - 1].suggestions : [];
        },
    },
};
</script>
<style lang="scss" scoped>
@import 'resources/scss/toolbox.scss';

.sc-chat-window {
    width: 400px;
    height: calc(100% - 120px);
    max-height: 590px;
    position: fixed;
    right: $size-4;
    bottom: calc(#{$size-1} + #{$size-4 * 2});
    box-sizing: border-box;
    background: var(--p-content-background);
    box-shadow: $card-shadow;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: 0.3s ease-in-out;
    border-radius: $radius-large;
    font-family: $family-primary;
}

.sc-chat-window__content {
    flex: 1;
    overflow-y: auto;
    margin: 10px;
    background: $scheme-main;
    border-radius: $radius;
}

.sc-chat-window__footer {
    .button.is-small {
        border-radius: 0 0 $radius-large $radius-large !important;
    }
}

.sc-chat-window.closed {
    opacity: 0;
    visibility: hidden;
    bottom: calc(#{$size-1} + #{$size-4});
}

.sc-message--me {
    text-align: right;
}

.sc-message--them {
    text-align: left;
}

@media (max-width: 450px) {
    .sc-chat-window {
        border: 0;
        width: 100%;
        height: 100%;
        max-height: 100%;
        right: 0;
        bottom: 0;
        border-radius: 0;
    }

    .sc-chat-window {
        transition: 0.1s ease-in-out;
    }

    .sc-chat-window.closed {
        bottom: 0;
    }
}
</style>
